// Recovery buttons for a work item (cancel / edit / retry / recover / reconcile), shared by the
// card footer and the Work screen. Every path is explicit; nothing is retried automatically.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { WorkView } from "@shared/api";
import { feedApi, type WorkRecovery } from "@web/lib/feedApi";
import { Button } from "@web/ui/Button";
import { recoverWork } from "@web/feed/cardOps";

const LABEL: Record<WorkRecovery, string> = {
  cancel: "Cancel",
  retry: "Try again",
  recover: "Recover",
  reconcile: "Check Gmail now",
  discard: "Don't run it",
};

const HINT: Record<WorkRecovery, string> = {
  cancel: "Stop it before it runs",
  retry: "Queue it again",
  recover: "Take it back from a stalled run",
  reconcile: "Look in Gmail for what really happened",
  discard: "Drop this approval; the card goes back to review",
};

export function QueuedNoteEditor({ work }: { work: WorkView }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(work.instruction);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const next = value.trim();
    if (!next || next === work.instruction) return;
    setSaving(true);
    try {
      await feedApi.editWork(work.id, next);
      toast("Note updated.");
      void queryClient.invalidateQueries({ queryKey: ["work"] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
    } catch {
      toast("Couldn't update the note: Claude may have started already.");
      setValue(work.instruction);
    } finally {
      setSaving(false);
    }
  };
  return (
    <label className="flex flex-col gap-1">
      <span className="mm-kicker">Your note</span>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === "Escape") e.currentTarget.blur();
        }}
        rows={Math.max(2, value.split("\n").length)}
        className="w-full resize-y rounded-lg border border-line-strong bg-paper px-3 py-2 text-sm text-ink"
      />
      <span className="text-xs text-muted">
        {saving ? "Saving…" : "You can change this until Claude picks it up."}
      </span>
    </label>
  );
}

export function WorkControls({ work, compact = false }: { work: WorkView; compact?: boolean }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<WorkRecovery | null>(null);
  const [editing, setEditing] = useState(false);
  const actions = work.recovery.filter((r): r is WorkRecovery => r !== "edit");
  const canEdit = work.recovery.includes("edit");
  if (!actions.length && !canEdit) return null;
  const run = async (action: WorkRecovery) => {
    setBusy(action);
    await recoverWork(queryClient, work, action);
    setBusy(null);
  };
  return (
    <div className="flex flex-col gap-2">
      {editing ? <QueuedNoteEditor key={`${work.id}:${work.instruction}`} work={work} /> : null}
      <div className="flex flex-wrap gap-2">
        {canEdit ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing((e) => !e)}>
            {editing ? "Done editing" : "Edit note"}
          </Button>
        ) : null}
        {actions.map((action) => (
          <Button
            key={action}
            size="sm"
            variant={action === "cancel" ? "ghost" : "secondary"}
            title={compact ? HINT[action] : undefined}
            busy={busy === action}
            disabled={busy !== null}
            onClick={() => void run(action)}
          >
            {LABEL[action]}
          </Button>
        ))}
      </div>
    </div>
  );
}
